<template>
  <div id="recurringDiv">
    <b-row v-bind="$attrs">
      <b-col cols="12">
        <h5>
          {{ recurringStatement }}
        </h5>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { currencyFormat, awsDateToDate } from "@/services/Formatting.js";

export default {
  props: {
    recurringInfo: {
      period: {
        type: Number,
        default: 0
      },
      frequency: {
        type: String,
        default: ""
      },
      reason: {
        type: String,
        default: ""
      },
      executionDate: {
        type: String,
        default: ""
      }
    },
    orderDetails: {
      amount: {
        type: Number,
        default: 0
      },
      currency: {
        type: String,
        default: ""
      },
      initialPaymentAmount: {
        type: Number,
        default: 0
      }
    },
    paymentFrequencies: {
      type: Object,
      default() {
        return {
          Daily: "Daily",
          Weekly: "Weekly",
          Fortnightly: "Fortnightly/Bi-Weekly",
          Monthly: "Monthly",
          BiMonthly: "Bi-Monthly",
          Quarterly: "Quarterly",
          Yearly: "Yearly"
        };
      }
    }
  },
  computed: {
    recurringStatement() {
      return this.generateRecurringStatement();
    },
    currencyText() {
      return this.orderDetails.currency;
    },
    hasInitialPaymentAmount() {
      return this.orderDetails.initialPaymentAmount > 0;
    },
    isInitialPaymentEqualToRecurringPayment() {
      return (
        this.hasInitialPaymentAmount &&
        this.orderDetails.initialPaymentAmount === this.orderDetails.amount
      );
    },
    recurringAmount() {
      return currencyFormat(this.orderDetails.amount);
    },
    initialPaymentAmount() {
      return currencyFormat(this.orderDetails.initialPaymentAmount);
    },
    executionDate() {
      const executionDateString = this.recurringInfo.executionDate;
      if (!executionDateString) return new Date();

      return awsDateToDate(executionDateString);
    }
  },
  methods: {
    generateRecurringStatement() {
      const firstAmount = this.hasInitialPaymentAmount
        ? this.initialPaymentAmount
        : this.recurringAmount;
      const secondAmount = this.recurringAmount;
      const currency = this.currencyText;

      const beginingPhrase = `A payment of ${firstAmount} ${currency} will`;
      const continuingPhrase = `be collected today and`;
      const recurringPhrase = "recur";
      const recurringPeriod = this.getRecurringPeriodText(
        this.recurringInfo.frequency,
        this.executionDate
      );
      const recurringEnding = `${secondAmount} ${currency} will be collected`;

      if (!this.hasInitialPaymentAmount) {
        return `${beginingPhrase} ${recurringPhrase} ${recurringPeriod}.`;
      }

      if (this.isInitialPaymentEqualToRecurringPayment) {
        return `${beginingPhrase} ${continuingPhrase} ${recurringPhrase} ${recurringPeriod}.`;
      }

      return `${beginingPhrase} ${continuingPhrase} ${recurringPeriod} ${recurringEnding}.`;
    },
    getRecurringPeriodText(frequency, executionDate) {
      const { day, weekDay, month } = this.parseDate(executionDate);

      switch (frequency) {
        case "Daily":
          return "everyday";
        case "Weekly":
          return `every ${weekDay}`;
        case "Fortnightly":
          return `every other ${weekDay}`;
        case "Monthly":
          return `on the ${day} of every month`;
        case "BiMonthly":
          return `on the ${day} of every other month`;
        case "Quarterly":
          return `on the ${day} of every 3rd month`;
        case "Yearly":
          return `on the ${day} of ${month} every year`;
        default:
          return "";
      }
    },
    parseDate(date) {
      const days = [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday"
      ];
      const months = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December"
      ];

      const weekDay = days[date.getDay()];
      const day = this.getDayWithOrdinal(date);
      const month = months[date.getMonth()];

      return { day, weekDay, month };
    },
    getDayWithOrdinal(date) {
      const day = date.getDate();
      let ordinal = "th";

      if (day <= 3 && day >= 21) {
        switch (d % 10) {
          case 1:
            ordinal = "st";
          case 2:
            ordinal = "nd";
          case 3:
            ordinal = "rd";
          default:
            ordinal = "th";
        }
      }

      return `${day}${ordinal}`;
    }
  }
};
</script>

<style lang="scss" scoped>
#r {
  font-size: 15px;
  font-weight: 400;
}
</style>
